import { FC, useState } from "react";
import type { ParabolicSARSettings } from "../../model";
import styles from "./AcceleratorOscillatorInputsTab.module.css";

type InputKey = "acceleration" | "maxAcceleration";
type InputValues = Record<InputKey, string>;

const FIELDS: { key: InputKey; label: string; min: number; max: number; step: number }[] = [
  { key: "acceleration", label: "Acceleration", min: 0.001, max: 10, step: 0.001 },
  { key: "maxAcceleration", label: "Maximum Acceleration", min: 0.1, max: 10, step: 0.1 },
];

interface Props {
  settings: ParabolicSARSettings;
  onChange: (settings: ParabolicSARSettings) => void;
}

export const ParabolicSARInputsTab: FC<Props> = ({ settings, onChange }) => {
  const [values, setValues] = useState<InputValues>({
    acceleration: String(settings.acceleration),
    maxAcceleration: String(settings.maxAcceleration),
  });

  const handleChange = (key: InputKey, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleBlur = (key: InputKey) => {
    const field = FIELDS.find((f) => f.key === key)!;
    const parsed = parseFloat(values[key]);
    const clamped = isNaN(parsed) ? settings[key] : Math.min(Math.max(field.min, parsed), field.max);
    const rounded = parseFloat(clamped.toFixed(3));
    setValues((prev) => ({ ...prev, [key]: String(rounded) }));
    onChange({ ...settings, [key]: rounded });
  };

  return (
    <div className={styles.tab}>
      {FIELDS.map(({ key, label, min, max, step }) => (
        <div key={key} className={styles.row}>
          <span className={styles.label}>{label}</span>
          <input
            type="number"
            className={styles.input}
            min={min}
            max={max}
            step={step}
            value={values[key]}
            onChange={(e) => handleChange(key, e.target.value)}
            onBlur={() => handleBlur(key)}
          />
        </div>
      ))}
    </div>
  );
};
